// src/App.jsx
import React, { lazy, Suspense, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ErrorBoundary } from '@sentry/react';
import { useParams } from 'react-router-dom';

import Shortcode from './Shortcode';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorComponent from './ErrorComponent';
import Fallback from './Fallback.jsx';
import SingleProduct from './views/SingleProduct';
import SweedSpike from './views/SweedSpike'; // THROWAWAY spike (activated by ?sweed=1)

import { useConfig } from './contexts/ConfigContext';
import { useAppState } from './contexts/AppStateContext';
import { useCart } from './contexts/CartContext';
import { useRetailerData } from './hooks/dutchie-queries.js';
import { useInitialURLParams } from './hooks/useInitialURLParams';

const CartSidebarIcon = lazy(() => import('./components/Cart/CartSidebarIcon'));
const CartSidebarWrapper = lazy(() => import('./components/Cart/CartSidebarWrapper'));
const LocationModal = lazy(() => import('./components/LocationSelect/LocationModal'));

const App = ({ fallback }) => {
    const initialParams = useInitialURLParams();
    const params = useParams();

    // Get all needed values from contexts
    const {
        retailers,
        nonHeadlessRetailers,
        shortcodeData,
        themeConfig,
    } = useConfig();

    const {
        isInitialized,
        isAppReady,
        retailerId,
        setRetailerId,
        currentRetailer,
        nonHeadlessRetailer,
        setNonHeadlessRetailer,
        nonHeadlessRetailerData,
        setNonHeadlessRetailerData,
        fallbackEnabled,
        gatesPassed,
        needsLocationGate,
        needsAgeGate,
        passAgeGate,
        shouldRenderCart,
        productPopupData,
    } = useAppState();

    const { isFetched: isFetchedCartData } = useCart();
    const { isFetched: isFetchedRetailerData } = useRetailerData(retailerId);

    // Manage <link rel="canonical"> without react-helmet (its SideEffect wrapper uses
    // the deprecated UNSAFE_componentWillMount, which warns under StrictMode).
    useEffect(() => {
        let link = document.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', 'canonical');
            document.head.appendChild(link);
        }
        link.setAttribute('href', window.location.href);
    }, [params]);

    // THROWAWAY spike: bypass all gates and render the Sweed data path when ?sweed=1.
    // Placed after all hook calls above so hook order stays valid.
    // HARD-DISABLED for production (Sweed is in early development). The `&& false`
    // guard guarantees the spike can never render on a live site, regardless of the
    // ?sweed=1 URL param. Remove `&& false` to resume Sweed spike development.
    if (false && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('sweed') === '1') {
        const firstDataset = Object.values(shortcodeData || {})[0];
        return (
            <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                <SweedSpike shortcodeData={firstDataset} />
            </ErrorBoundary>
        );
    }

    // Check if this is a single product page for SEO purposes
    const isSingleProductPage = params?.categoryType === 'product';

    // For SEO: Determine if we should render content in the background
    const shouldRenderContentForSEO = isSingleProductPage && 
        isInitialized && 
        retailerId && 
        !fallbackEnabled;

    // Simple state checks - no complex logic
    if (!isInitialized) {
        // Determine appropriate loading type based on shortcode
        const getLoadingType = () => {
            // Get the first shortcode type from shortcodeData
            const firstDataset = Object.values(shortcodeData)[0];
            const shortcode = firstDataset?.shortcode;

            switch (shortcode) {
                case 'shopnav':
                case 'shopnavfull':
                    return 'shopNav';
                case 'headlessmenu':
                    return 'menuList';
                default:
                    return 'list';
            }
        };

        return <LoadingSpinner loadingType={getLoadingType()} />;
    }

    // Fallback mode
    if (fallbackEnabled && retailerId) {
        return (
            <>
                {Object.entries(shortcodeData).map(([rootId, dataset], i) => {
                    const root = document.getElementById(rootId);
                    const fallbackElement = (
                        <Fallback
                            key={rootId}
                            rootId={rootId}
                            shortcodeData={dataset}
                            script={fallback[retailerId].script}
                        />
                    );

                    return i === 0 ? fallbackElement : createPortal(fallbackElement, root);
                })}
            </>
        );
    }

    // Gates not passed - but render content for SEO if it's a single product page
    if (!gatesPassed && !nonHeadlessRetailer) {
        return (
            <>
                {/* Always show the location modal for user interaction */}
                {createPortal(
                    <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                        <Suspense fallback={null}>
                            <LocationModal
                                retailers={retailers}
                                retailerId={retailerId}
                                onRetailerSelect={setRetailerId}
                                onAgeGatePass={passAgeGate}
                                showLocationGate={needsLocationGate}
                                showAgeGate={needsAgeGate && !needsLocationGate}
                                nonHeadlessRetailer={nonHeadlessRetailer}
                                setNonHeadlessRetailer={setNonHeadlessRetailer}
                                nonHeadlessRetailerData={nonHeadlessRetailerData}
                                setNonHeadlessRetailerData={setNonHeadlessRetailerData}
                            />
                        </Suspense>
                    </ErrorBoundary>,
                    document.body
                )}

                {/* Render content for SEO if it's a single product page */}
                {shouldRenderContentForSEO && (
                    <div style={{ 
                        position: 'absolute', 
                        left: '-9999px', 
                        top: '-9999px',
                        visibility: 'hidden',
                        // Ensure it's still accessible to screen readers and crawlers
                        clip: 'rect(0, 0, 0, 0)',
                        height: '1px',
                        overflow: 'hidden',
                        width: '1px'
                    }}>
                        <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                            {/* Render shortcodes for SEO */}
                            {Object.entries(shortcodeData).map(([rootId, dataset], i) => {
                                const shortcodeElement = (
                                    <Shortcode
                                        key={`seo-${rootId}`}
                                        rootId={rootId}
                                        shortcodeData={dataset}
                                        forcedOrderType={initialParams}
                                    />
                                );
                                return shortcodeElement;
                            })}
                        </ErrorBoundary>
                    </div>
                )}
            </>
        );
    }

    // Waiting for data
    if (!isAppReady || (!isFetchedRetailerData && retailerId) || (!isFetchedCartData && retailerId)) {
        // Determine appropriate loading type based on shortcode
        const getLoadingType = () => {
            const firstDataset = Object.values(shortcodeData)[0];
            const shortcode = firstDataset?.shortcode;

            switch (shortcode) {
                case 'shopnav':
                case 'shopnavfull':
                    return 'shopNav';
                case 'headlessmenu':
                    return 'menuList';
                default:
                    return 'list';
            }
        };

        return <LoadingSpinner loadingType={getLoadingType()} />;
    }

    // The popup lives outside the shortcode roots, so give it the same wrapper
    // classes the PHP shortcode template applies (see Shortcodes::shortcode_template).
    const popupWrapperClass = [
        'range-headless',
        themeConfig?.cardTheme ? `headless-card-theme-${themeConfig.cardTheme}` : '',
    ].filter(Boolean).join(' ');

    // Main app render - just display components
    return (
        <>
            <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                {/* Render shortcodes */}
                {Object.entries(shortcodeData).map(([rootId, dataset], i) => {
                    const root = document.getElementById(rootId);
                    const shortcodeElement = (
                        <Shortcode
                            key={rootId}
                            rootId={rootId}
                            shortcodeData={dataset}
                            forcedOrderType={initialParams}
                        />
                    );

                    return i === 0 ? shortcodeElement : createPortal(shortcodeElement, root);
                })}

                {/* Render cart sidebar if needed */}
                {shouldRenderCart && createPortal(
                    <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                        <Suspense fallback={null}>
                            <CartSidebarIcon fixedCart={true} />
                            <CartSidebarWrapper fixedCart={true} forcedOrderType={initialParams} />
                        </Suspense>
                    </ErrorBoundary>,
                    document.body
                )}

                {/* Single product popup: rendered once per page rather than once per
                    menu/products shortcode, so pages with several shortcodes never
                    stack duplicate dialogs. */}
                {productPopupData && createPortal(
                    <div className={popupWrapperClass}>
                        <ErrorBoundary
                            fallback={(props) => (
                                <ErrorComponent
                                    {...props}
                                    componentName="SingleProduct"
                                    parentName="App"
                                />
                            )}
                        >
                            <SingleProduct />
                        </ErrorBoundary>
                    </div>,
                    document.body
                )}
            </ErrorBoundary>
        </>
    );
};

export default App;
